/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { useEffect, useState } from "react";

import { CosmosTitle, EdsIcon } from "@components/elements";
import { scrollIntoViewEffect } from "@components/utils/scroll.utils";
import { useLocation } from "@epic-mw/app-context";
import { css, cx } from "@panda/css";
import { HStack } from "@panda/jsx";

import { TierCtaCard } from "./AccordionTierCard.CtaCard";
import { TierFeatureCard } from "./AccordionTierCard.FeatureCard";
import { tierItemStyles } from "./AccordionTierCard.styles";

import type { AccordionTierCardFragment } from "@cosmos/hygraph-schema";

// Presentational pill — purpose-built non-interactive replacement for CosmosTag inside
// the accordion trigger. CosmosTag renders a `<button>` (via CosmosLinkButton, even with
// a falsy href), and Radix Accordion.Trigger is also a `<button>`. Nesting the two
// produces a React hydration error ("button cannot be a descendant of button"). The
// schema's `tag` field is a plain string — we just need the visual treatment.
const tierTagStyles = css({
	display: "inline-flex",
	alignItems: "center",
	backgroundColor: "eds.fill.secondary.default",
	color: "eds.text.contrast",
	borderRadius: "eds.borderRadius.full",
	paddingBlock: "eds.dimension.2",
	paddingInline: "eds.dimension.12",
	textStyle: "uiMD",
	whiteSpace: "nowrap",
});

export interface AccordionTierCardProps extends AccordionTierCardFragment {
	/** Index used to derive a stable Radix value when no `id` is provided. */
	index?: number;
}

/**
 * Single tier (one accordion item). Built on Radix `Accordion.Item`/`Header`/`Trigger`/
 * `Content` so it inherits the WAI-ARIA accordion pattern, keyboard navigation, and
 * animated open/close transitions automatically.
 *
 * The trigger row renders the tier title + optional `Tag` pill (e.g. "Paid") + a chevron
 * icon. The content panel renders one or more `TierFeatureCard`s plus an optional
 * `TierCtaCard` ("Ready to get started…") at the bottom.
 *
 * Hash-based deep linking: when `anchorLinkId` (or `id`) matches `window.location.hash`
 * on mount, the tier is expanded and scrolled into view.
 */
export const AccordionTierCard = ({
	id,
	index,
	title,
	tag,
	gradient,
	defaultOpen,
	anchorLinkId,
	items,
}: AccordionTierCardProps): React.JSX.Element | null => {
	// All hooks must run before any early return — moving the title-empty guard below
	// keeps `useState` / `useEffect` / `useLocation` in a stable order across renders
	// (React Hooks rules-of-hooks).
	const itemId = id || `tier-${index ?? 0}`;
	const initial = defaultOpen && itemId ? [itemId] : [];
	const [value, setValue] = useState<string[]>(initial);
	const location = useLocation();
	const isExpanded = value.includes(itemId);

	useEffect(() => {
		const hash = location.hash?.replace(/^#/, "");
		if (!hash) return;
		if (hash === anchorLinkId || hash === itemId) {
			setValue([itemId]);
			scrollIntoViewEffect(anchorLinkId || itemId);
		}
	}, [location.hash, anchorLinkId, itemId]);

	if (!title?.html && !title?.text) return null;

	return (
		<AccordionPrimitive.Root type="multiple" value={value} onValueChange={setValue} id={anchorLinkId || itemId}>
			<AccordionPrimitive.Item className={tierItemStyles({ expanded: isExpanded })} value={itemId}>
				<AccordionPrimitive.Header asChild>
					<h3>
						<AccordionPrimitive.Trigger className="tier-trigger">
							<HStack
								className="tier-trigger-row"
								justifyContent="space-between"
								alignItems="center"
								gap="eds.dimension.8"
							>
								<HStack gap="eds.dimension.12" alignItems="center" flex="1" minWidth="0">
									<CosmosTitle title={{ html: title.html, text: title.text, as: "span", variant: "headingMD" }} />
									{tag ? <span className={tierTagStyles}>{tag}</span> : null}
								</HStack>
								<div className={cx("tier-trigger-icon")}>
									<EdsIcon icon="ChevronDown" color="primary" size="xs" className="tier-trigger-chevron" />
								</div>
							</HStack>
						</AccordionPrimitive.Trigger>
					</h3>
				</AccordionPrimitive.Header>
				<AccordionPrimitive.Content className="tier-content">
					<div className="tier-content-inner">
						{items.map((card, i) => {
							const key = card.id || `tier-${itemId}-card-${i}`;
							// Discriminate on `__typename` — both union members have it as a required
							// literal, so TypeScript narrows correctly with no cast. An unrecognized
							// `__typename` (e.g. a future card type added to the union) falls through
							// to `null` rather than rendering as a feature card by accident.
							if (card.__typename === "CosmosElementAccordionTierCtaCard") {
								return <TierCtaCard key={key} {...card} fallbackGradient={gradient} />;
							}
							if (card.__typename === "CosmosElementAccordionTierFeatureCard") {
								return <TierFeatureCard key={key} {...card} />;
							}
							return null;
						})}
					</div>
				</AccordionPrimitive.Content>
			</AccordionPrimitive.Item>
		</AccordionPrimitive.Root>
	);
};

AccordionTierCard.displayName = "AccordionTierCard";
